import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useCallingMultiplier, resolveBoostedMultiplier } from "./useCallingMultiplier";

export interface UserXP {
  id: string;
  user_id: string;
  total_xp: number;
  current_level: number;
  xp_to_next_level: number;
  created_at: string;
  updated_at: string;
}

export interface XPTransaction {
  id: string;
  user_id: string;
  xp_amount: number;
  action_type: string;
  description: string | null;
  created_at: string;
}

export interface Milestone {
  id: string;
  name: string;
  description: string | null;
  category: string;
  xp_reward: number;
  icon: string | null;
  badge_color: string | null;
  sort_order: number;
  created_at: string;
}

export interface UserMilestone {
  id: string;
  user_id: string;
  milestone_id: string;
  completed_at: string;
  awarded_xp: number;
  milestone?: Milestone;
}

// XP calculation helpers
const calculateXPForLevel = (level: number): number => {
  return Math.floor(100 * Math.pow(1.5, level - 1));
};

const calculateLevelFromXP = (totalXP: number): { level: number; xpToNext: number } => {
  let level = 1;
  let xpRequired = 100;
  let xpAccumulated = 0;

  while (xpAccumulated + xpRequired <= totalXP) {
    xpAccumulated += xpRequired;
    level++;
    xpRequired = calculateXPForLevel(level);
  }

  return {
    level,
    xpToNext: xpRequired - (totalXP - xpAccumulated)
  };
};

// Hook to get user's XP data
export function useUserXP() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["userXP", user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from("user_xp")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;

      // If no XP record exists, create one
      if (!data) {
        const { data: newXP, error: insertError } = await (supabase as any).rpc("award_xp", { _amount: 0, _action_type: "init" });
        if (insertError) throw insertError;
        return newXP as UserXP;
      }

      return data as UserXP;
    },
    enabled: !!user
  });
}

// Hook to add XP
export function useAddXP() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { callingPath } = useCallingMultiplier();

  return useMutation({
    mutationFn: async ({ 
      amount, 
      actionType, 
      description 
    }: { 
      amount: number; 
      actionType: string; 
      description?: string;
    }) => {
      if (!user) throw new Error("Must be logged in");

      // Calling Bonus — boosts XP when the action aligns with the user's vowed
      // path. Throttled by per-action cooldown + daily diminishing returns to
      // prevent farming the same action over and over.
      const baseAmount = amount;
      const { multiplier: mult, reason } = await resolveBoostedMultiplier({
        userId: user.id,
        actionType,
        callingPath,
        ledger: "xp",
      });
      const boostedAmount = Math.round(baseAmount * mult);
      const bonus = boostedAmount - baseAmount;
      const boosted = bonus > 0;
      const finalDescription = boosted
        ? `${description ?? actionType} · +${bonus} Calling Bonus`
        : description;

      const { data: before } = await supabase.from("user_xp").select("current_level").eq("user_id", user.id).maybeSingle();
      const previousLevel = before?.current_level || 1;
      const { data: row, error: rpcErr } = await (supabase as any).rpc("award_xp", {
        _amount: boostedAmount, _action_type: actionType, _description: finalDescription ?? null,
      });
      if (rpcErr) throw rpcErr;
      const newTotalXP = row?.total_xp ?? 0;
      const level = row?.current_level ?? previousLevel;

      return { 
        newTotalXP, 
        level, 
        previousLevel,
        leveledUp: level > previousLevel,
        boosted,
        bonus,
        callingPath,
        throttleReason: reason,
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["userXP"] });
      queryClient.invalidateQueries({ queryKey: ["xpTransactions"] });
      
      if (data.boosted) {
        toast.success(`+${data.bonus} XP Calling Bonus`, {
          description: "Reward for pursuing your chosen path.",
        });
      } else if (data.callingPath && data.throttleReason === "cooldown") {
        toast.message("Calling Bonus on cooldown", {
          description: "Switch it up — bonus refreshes in about a minute.",
        });
      } else if (data.callingPath && data.throttleReason === "exhausted") {
        toast.message("Calling Bonus cap reached for today", {
          description: "You've earned the full daily boost on this action.",
        });
      }

      if (data.leveledUp) {
        toast.success(`Advancement! You've reached Tier ${data.level}`);
      }
    },
    onError: (error) => {
      console.error("Error adding progress:", error);
      toast.error("Failed to add progress");
    }
  });
}

// Hook to get XP transaction history
export function useXPTransactions() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["xpTransactions", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from("xp_transactions")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as XPTransaction[];
    },
    enabled: !!user
  });
}

// Hook to get all milestones
export function useMilestones() {
  return useQuery({
    queryKey: ["milestones"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("milestones")
        .select("*")
        .order("sort_order", { ascending: true });

      if (error) throw error;
      return data as Milestone[];
    }
  });
}

// Hook to get user's completed milestones
export function useUserMilestones() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["userMilestones", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from("user_milestones")
        .select(`
          *,
          milestone:milestones(*)
        `)
        .eq("user_id", user.id)
        .order("completed_at", { ascending: false });

      if (error) throw error;
      return data as UserMilestone[];
    },
    enabled: !!user
  });
}

// Hook to complete a milestone
export function useCompleteMilestone() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const addXP = useAddXP();

  return useMutation({
    mutationFn: async (milestoneId: string) => {
      if (!user) throw new Error("Must be logged in");

      // Get milestone details
      const { data: milestone, error: milestoneError } = await supabase
        .from("milestones")
        .select("*")
        .eq("id", milestoneId)
        .single();

      if (milestoneError) throw milestoneError;

      // Check if already completed
      const { data: existing } = await supabase
        .from("user_milestones")
        .select("id")
        .eq("user_id", user.id)
        .eq("milestone_id", milestoneId)
        .maybeSingle();

      if (existing) {
        throw new Error("Milestone already completed");
      }

      // Complete milestone
      const { error: insertError } = await supabase
        .from("user_milestones")
        .insert({
          user_id: user.id,
          milestone_id: milestoneId,
          awarded_xp: milestone.xp_reward
        });

      if (insertError) throw insertError;

      return milestone as Milestone;
    },
    onSuccess: async (milestone) => {
      queryClient.invalidateQueries({ queryKey: ["userMilestones"] });
      
      // Award XP
      await addXP.mutateAsync({
        amount: milestone.xp_reward,
        actionType: "milestone",
        description: `Completed milestone: ${milestone.name}`
      });

      toast.success(`🏆 Milestone unlocked: ${milestone.name}!`);
    },
    onError: (error) => {
      if (error.message !== "Milestone already completed") {
        toast.error("Failed to complete milestone");
      }
    }
  });
}
